import React, { useEffect, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import { useEvent } from "../lib/event";
import { useAttraction } from "../lib/attraction";
import DashboardLayout from "../components/DashboardLayout";
import { formatOrdinalDate } from "../lib/timeFormat";
import { notify } from "../lib/notify";
import { Plus, Trash2, ArrowUp, ArrowDown, Megaphone, Tag, Save, Link2, Copy, Check, BarChart3, ExternalLink, Pencil, HelpCircle, ChevronDown } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function MarketingPage() {
  const { currentEvent, currentEventId } = useEvent();
  const { attraction, refresh: refreshAttraction } = useAttraction() || {};
  const [types, setTypes] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!currentEventId) { setTypes(null); return; }
    setErr("");
    try {
      const r = await api.get(`/events/${currentEventId}`);
      setTypes((r.data.ticket_types || []).map((t) => ({ ...t })));
    } catch (e) { setErr("Failed to load ticket types"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [currentEventId]);

  if (!currentEventId) {
    return (
      <DashboardLayout title="Marketing" subtitle="Dynamic pricing and promotions">
        <div className="st-card p-8 text-center text-[#8a8a8a]" data-testid="marketing-no-event">
          Select or create an event to manage its marketing settings.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Marketing"
      subtitle={currentEvent ? `Tiered pricing & promotions · ${currentEvent.name}` : "Tiered pricing & promotions"}
    >
      <div className="max-w-3xl" data-testid="marketing-page">
        <div className="st-card p-4 mb-5 flex items-start gap-3" data-testid="marketing-intro">
          <div className="mt-0.5 text-[#dc2626]"><Megaphone size={20} /></div>
          <div>
            <div className="font-semibold">Tiered pricing</div>
            <p className="text-[13px] text-[#9a9a9a] mt-1">
              Reward early bookers or create urgency with time- or volume-based pricing. Tiers are
              evaluated top to bottom — the first matching tier sets the live price. Once every tier
              expires, your standard base price applies.
            </p>
          </div>
        </div>

        {err && <div className="st-card p-4 mb-4 text-[#fca5a5] bg-[#2a1212] border-[#5b1919]" data-testid="marketing-error">{err}</div>}

        {types === null ? (
          <div className="st-card p-8 text-center text-[#8a8a8a]">Loading…</div>
        ) : types.length === 0 ? (
          <div className="st-card p-8 text-center text-[#8a8a8a]" data-testid="marketing-no-types">
            No ticket types yet. Add ticket types first, then set up tiered pricing here.
          </div>
        ) : (
          <div className="space-y-4">
            {types.map((t) => (
              <TicketTierCard key={t.id} eventId={currentEventId} ticketType={t} onSaved={load} />
            ))}
          </div>
        )}

        <div className="st-card p-4 mt-6 flex items-start gap-3 opacity-70" data-testid="marketing-discount-soon">
          <div className="mt-0.5 text-[#8a8a8a]"><Tag size={18} /></div>
          <div>
            <div className="font-semibold text-[#cfcfcf]">Discount codes</div>
            <p className="text-[13px] text-[#8a8a8a] mt-1">Coming soon — create promo codes for customers at checkout.</p>
          </div>
        </div>

        <TrackingHelp />
        <WebsiteTracking attractionSlug={attraction?.slug} />
        <BookingButtonGenerator attractionSlug={attraction?.slug} event={currentEvent} />
        <UtmBuilder attractionSlug={attraction?.slug} event={currentEvent}
                    savedOwnUrl={attraction?.marketing_own_url || ""} onOwnUrlSaved={refreshAttraction} />
      </div>
    </DashboardLayout>
  );
}

function TicketTierCard({ eventId, ticketType, onSaved }) {
  const initial = (ticketType.tiers || []).map((x) => ({ _key: uid(), ...x }));
  const [tiers, setTiers] = useState(initial);
  const [saving, setSaving] = useState(false);
  const base = Number(ticketType.base_price_pence) || 0;

  const dirty = JSON.stringify(tiers.map(({ _key, ...r }) => r)) !==
    JSON.stringify((ticketType.tiers || []).map(({ _key, ...r }) => r));

  const addTier = () => setTiers([...tiers, {
    _key: uid(), label: "", price_mode: "fixed", tier_price_pence: Math.max(0, base - 200),
    discount_percent: 10, discount_amount_pence: 200,
    expiry_type: "date", cutoff_date: "", ticket_cap: null, cap_enabled: false, is_active: true,
    show_remaining_count: true, show_remaining_days: false,
  }]);
  const updateTier = (key, patch) => setTiers(tiers.map((x) => x._key === key ? { ...x, ...patch } : x));
  const removeTier = (key) => setTiers(tiers.filter((x) => x._key !== key));
  const moveTier = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= tiers.length) return;
    const copy = [...tiers];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    setTiers(copy);
  };

  const save = async () => {
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (!(t.label || "").trim()) { notify.error(`Tier ${i + 1}: add a name`); return; }
      const needsDate = (t.expiry_type || "date") === "date" || t.expiry_type === "either";
      const needsCount = t.expiry_type === "count" || t.expiry_type === "either";
      if (needsDate && !t.cutoff_date) { notify.error(`Tier ${i + 1} (${t.label}): choose an end date`); return; }
      if (needsCount && t.cap_enabled && !t.ticket_cap) { notify.error(`Tier ${i + 1} (${t.label}): enter a ticket cap`); return; }
    }
    setSaving(true);
    try {
      const payload = tiers.map(({ _key, ...rest }, i) => ({ ...rest, sort_order: i }));
      await api.patch(`/events/${eventId}/ticket-types/${ticketType.id}`, { tiers: payload });
      notify.success(`Pricing tiers saved for ${ticketType.name}`);
      onSaved && onSaved();
    } catch (e) {
      notify.error(formatErr(e) || "Failed to save tiers");
    } finally { setSaving(false); }
  };

  return (
    <div className="st-card p-4" data-testid={`marketing-tt-${ticketType.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold">
          {ticketType.name}{" "}
          <span className="text-xs text-[#8a8a8a]">Base {poundsFromPence(base)}</span>
          {tiers.length > 0 && <span className="st-chip st-chip-red ml-2">{tiers.length} tier{tiers.length === 1 ? "" : "s"}</span>}
        </div>
        <button className="st-btn st-btn-ghost text-xs" onClick={addTier} data-testid={`marketing-add-tier-${ticketType.id}`}>
          <Plus size={14} /> Add tier
        </button>
      </div>

      {tiers.length === 0 && <p className="text-xs text-[#7a7a7a]">No tiers yet — the base price applies to everyone.</p>}

      <div className="space-y-3">
        {tiers.map((tier, k) => (
          <TierRow key={tier._key} tier={tier} ttId={ticketType.id} idx={k} count={tiers.length} base={base} sold={ticketType.sold_count || 0}
                   onChange={(patch) => updateTier(tier._key, patch)}
                   onRemove={() => removeTier(tier._key)}
                   onMove={(dir) => moveTier(k, dir)} />
        ))}
      </div>

      {tiers.length > 0 && (
        <div className="text-xs text-[#8a8a8a] mt-3 pt-3 border-t border-[#1a1a1a]">
          After all tiers expire: <span className="text-[#dadada] font-medium">{poundsFromPence(base)}</span> (your standard price)
        </div>
      )}

      <div className="flex items-center justify-end gap-3 mt-4 pt-3 border-t border-[#1a1a1a]">
        {dirty && <span className="text-xs text-[#d4a64a]">Unsaved changes</span>}
        <button className="st-btn st-btn-primary text-sm" onClick={save} disabled={saving || !dirty}
                data-testid={`marketing-save-${ticketType.id}`}>
          <Save size={14} /> {saving ? "Saving…" : "Save tiers"}
        </button>
      </div>
    </div>
  );
}

function resolveTierPrice(tier, base) {
  const mode = tier.price_mode || "fixed";
  if (mode === "percent") return Math.max(0, base - Math.round(base * (Number(tier.discount_percent) || 0) / 100));
  if (mode === "amount") return Math.max(0, base - (Number(tier.discount_amount_pence) || 0));
  return Math.max(0, Number(tier.tier_price_pence) || 0);
}

function TierRow({ tier, ttId, idx, count, base, sold = 0, onChange, onRemove, onMove }) {
  const [priceStr, setPriceStr] = useState(tier.tier_price_pence != null ? (Number(tier.tier_price_pence) / 100).toFixed(2) : "");
  const [pctStr, setPctStr] = useState(tier.discount_percent != null ? String(tier.discount_percent) : "");
  const [amtStr, setAmtStr] = useState(tier.discount_amount_pence != null ? (Number(tier.discount_amount_pence) / 100).toFixed(2) : "");
  const mode = tier.price_mode || "fixed";
  const et = tier.expiry_type || "date";
  const showDate = et === "date" || et === "either";
  const showCount = et === "count" || et === "either";

  const onPrice = (v) => {
    setPriceStr(v);
    const pence = Math.round(parseFloat(v || 0) * 100);
    onChange({ tier_price_pence: isNaN(pence) ? 0 : pence });
  };
  const onPct = (v) => {
    setPctStr(v);
    let n = parseFloat(v || 0);
    if (isNaN(n) || n < 0) n = 0;
    if (n > 100) n = 100;
    onChange({ discount_percent: n });
  };
  const onAmt = (v) => {
    setAmtStr(v);
    const pence = Math.round(parseFloat(v || 0) * 100);
    onChange({ discount_amount_pence: isNaN(pence) ? 0 : Math.max(0, pence) });
  };
  const onCap = (raw) => {
    if (raw === "") return onChange({ ticket_cap: null });
    let n = parseInt(raw, 10);
    if (isNaN(n) || n < 1) n = 1;
    onChange({ ticket_cap: n });
  };

  const preview = resolveTierPrice(tier, base);

  return (
    <div className={`rounded-lg border p-3 ${tier.is_active === false ? "border-[#2b2b2b] bg-[#0c0c0c] opacity-70" : "border-[#262626] bg-[#0e0e0e]"}`} data-testid={`marketing-tier-${ttId}-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-1 pt-5">
          <button onClick={() => onMove(-1)} disabled={idx === 0} className="text-[#7a7a7a] hover:text-white disabled:opacity-30" data-testid={`marketing-tier-up-${ttId}-${idx}`}><ArrowUp size={14} /></button>
          <button onClick={() => onMove(1)} disabled={idx === count - 1} className="text-[#7a7a7a] hover:text-white disabled:opacity-30" data-testid={`marketing-tier-down-${ttId}-${idx}`}><ArrowDown size={14} /></button>
        </div>

        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#8a8a8a]">Tier name</label>
            <input className="st-input mt-1" placeholder="e.g. Early Bird, Last Minute" value={tier.label || ""}
                   data-testid={`marketing-tier-label-${ttId}-${idx}`}
                   onChange={(e) => onChange({ label: e.target.value })} />
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-[#8a8a8a]">Pricing for this tier</label>
            <div className="flex flex-wrap gap-3 mt-1.5">
              {[
                { v: "fixed", l: "Set a ticket price" },
                { v: "percent", l: "% discount" },
                { v: "amount", l: "Amount off (£)" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name={`mode-${ttId}-${idx}`} checked={mode === opt.v}
                         data-testid={`marketing-tier-mode-${opt.v}-${ttId}-${idx}`}
                         onChange={() => onChange({ price_mode: opt.v })} />
                  <span>{opt.l}</span>
                </label>
              ))}
            </div>

            {mode === "fixed" && (
              <div className="mt-2">
                <input type="number" step="0.01" min="0" className="st-input" placeholder="£ price" value={priceStr}
                       data-testid={`marketing-tier-price-${ttId}-${idx}`}
                       onChange={(e) => onPrice(e.target.value)} />
                <p className="text-[10px] text-[#7a7a7a] mt-1">The exact price charged during this tier.</p>
              </div>
            )}
            {mode === "percent" && (
              <div className="mt-2">
                <div className="relative">
                  <input type="number" step="1" min="0" max="100" className="st-input pr-7" placeholder="e.g. 20" value={pctStr}
                         data-testid={`marketing-tier-percent-${ttId}-${idx}`}
                         onChange={(e) => onPct(e.target.value)} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a8a8a] text-sm">%</span>
                </div>
                <p className="text-[10px] text-[#7a7a7a] mt-1">Off the ticket's base price on each date. Now: <span className="text-[#dadada]">{poundsFromPence(preview)}</span> (base {poundsFromPence(base)}).</p>
              </div>
            )}
            {mode === "amount" && (
              <div className="mt-2">
                <input type="number" step="0.01" min="0" className="st-input" placeholder="£ off (e.g. 5.00)" value={amtStr}
                       data-testid={`marketing-tier-amount-${ttId}-${idx}`}
                       onChange={(e) => onAmt(e.target.value)} />
                <p className="text-[10px] text-[#7a7a7a] mt-1">Subtracted from the base price on each date. Now: <span className="text-[#dadada]">{poundsFromPence(preview)}</span> (base {poundsFromPence(base)}).</p>
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-[#8a8a8a]">This tier ends when:</label>
            <div className="flex flex-wrap gap-3 mt-1.5">
              {[
                { v: "date", l: "A date is reached" },
                { v: "count", l: "Tickets sold reaches a limit" },
                { v: "either", l: "Either (whichever comes first)" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name={`met-${ttId}-${idx}`} checked={et === opt.v}
                         data-testid={`marketing-tier-et-${opt.v}-${ttId}-${idx}`}
                         onChange={() => onChange({ expiry_type: opt.v })} />
                  <span>{opt.l}</span>
                </label>
              ))}
            </div>
          </div>

          {showDate && (
            <div>
              <label className="text-xs text-[#8a8a8a]">Tier ends on</label>
              <input type="date" className="st-input mt-1" value={tier.cutoff_date || ""}
                     data-testid={`marketing-tier-cutoff-${ttId}-${idx}`}
                     onChange={(e) => onChange({ cutoff_date: e.target.value })} />
              <p className="text-[10px] text-[#7a7a7a] mt-1">
                {tier.cutoff_date ? `After ${formatOrdinalDate(tier.cutoff_date, { weekday: false })}, the next tier price applies.` : "After this date, the next tier price applies."}
              </p>
              <label className="flex items-center gap-2 text-[11px] text-[#9a9a9a] cursor-pointer mt-2">
                <input type="checkbox" checked={!!tier.show_remaining_days}
                       data-testid={`marketing-tier-showdays-${ttId}-${idx}`}
                       onChange={(e) => onChange({ show_remaining_days: e.target.checked })} />
                Show countdown to customers (e.g. "ends in 6 days")
              </label>
            </div>
          )}

          {showCount && (
            <div>
              <label className="flex items-center gap-2 text-xs text-[#8a8a8a] cursor-pointer">
                <input type="checkbox" checked={!!tier.cap_enabled}
                       data-testid={`marketing-tier-capenabled-${ttId}-${idx}`}
                       onChange={(e) => onChange({ cap_enabled: e.target.checked })} />
                Enable ticket cap for this tier
              </label>
              {tier.cap_enabled && (
                <>
                  <input type="number" min="1" className="st-input mt-1.5" value={tier.ticket_cap ?? ""} placeholder="e.g. 100"
                         data-testid={`marketing-tier-cap-${ttId}-${idx}`}
                         onChange={(e) => onCap(e.target.value)} />
                  <p className="text-[10px] text-[#7a7a7a] mt-1">After this many tickets are sold, the next tier applies.</p>
                  {tier.ticket_cap != null && Number(tier.ticket_cap) > 0 && (() => {
                    const cap = Number(tier.ticket_cap);
                    const used = Math.min(sold, cap);
                    const pct = Math.min(100, Math.round((sold / cap) * 100));
                    const soldOut = sold >= cap;
                    return (
                      <div className="mt-2" data-testid={`marketing-tier-progress-${ttId}-${idx}`}>
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="text-[#9a9a9a]">{used} / {cap} sold at this price</span>
                          <span className={soldOut ? "text-[#fca5a5]" : "text-[#d4a64a]"}>
                            {soldOut ? "Sold out — now at base price" : `${cap - sold} left`}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[#1f1f1f] overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${soldOut ? "bg-[#dc2626]" : "bg-[#d4a64a]"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                  <label className="flex items-center gap-2 text-[11px] text-[#9a9a9a] cursor-pointer mt-2">
                    <input type="checkbox" checked={tier.show_remaining_count !== false}
                           data-testid={`marketing-tier-showcount-${ttId}-${idx}`}
                           onChange={(e) => onChange({ show_remaining_count: e.target.checked })} />
                    Show tickets left to customers (e.g. "only 5 left at this price")
                  </label>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 pt-5">
          <label className="flex items-center gap-1.5 text-[11px] text-[#8a8a8a] cursor-pointer">
            <input type="checkbox" checked={tier.is_active !== false}
                   data-testid={`marketing-tier-active-${ttId}-${idx}`}
                   onChange={(e) => onChange({ is_active: e.target.checked })} />
            Active
          </label>
          <button onClick={onRemove} className="text-[#dc2626]" data-testid={`marketing-tier-del-${ttId}-${idx}`}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}


const UTM_PRESETS = [
  { label: "Facebook", source: "facebook", medium: "social" },
  { label: "Instagram", source: "instagram", medium: "social" },
  { label: "Google Ads", source: "google", medium: "cpc" },
  { label: "Email", source: "email", medium: "email" },
  { label: "Newsletter", source: "newsletter", medium: "email" },
  { label: "TikTok", source: "tiktok", medium: "social" },
];

function TrackingHelp() {
  const [open, setOpen] = useState(false);
  const Step = ({ n, title, children }) => (
    <div className="flex gap-3">
      <div className="shrink-0 w-6 h-6 rounded-full bg-[#1a0d0d] border border-[#3a1414] text-[#dc2626] text-xs font-bold grid place-items-center">{n}</div>
      <div className="text-[13px] text-[#c9c9c9]">
        <div className="font-semibold text-white">{title}</div>
        <div className="text-[#9a9a9a] mt-0.5">{children}</div>
      </div>
    </div>
  );
  return (
    <div className="st-card p-4 mt-6" data-testid="tracking-help">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3" data-testid="tracking-help-toggle">
        <div className="flex items-center gap-3">
          <div className="text-[#dc2626]"><HelpCircle size={20} /></div>
          <div className="text-left">
            <div className="font-semibold">How to add campaign tracking to your website</div>
            <p className="text-[13px] text-[#9a9a9a] mt-0.5">A 2-minute setup so you can see which ads and posts drive bookings.</p>
          </div>
        </div>
        <ChevronDown size={18} className={`text-[#8a8a8a] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4 pt-4 border-t border-[#1a1a1a] space-y-4" data-testid="tracking-help-body">
          <div className="rounded-lg bg-[#0f0f0f] border border-[#1e1e1e] p-3 text-[13px]">
            <span className="font-semibold text-white">First, the easy case:</span>
            <span className="text-[#9a9a9a]"> if your ads and posts link <span className="font-semibold text-[#c9c9c9]">straight to ScareTickets</span> (using a campaign link from the builder below), tracking is automatic — you don't need any of the steps below.</span>
          </div>

          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">If your ads point to your OWN website first</div>
          <div className="space-y-4">
            <Step n={1} title="Add the tracking script to your site">
              Copy the <span className="font-semibold">Website tracking script</span> below and paste it into your website's <span className="st-mono">&lt;head&gt;</span> (the same place you'd add Google Analytics). It captures the campaign tags when visitors land.
            </Step>
            <Step n={2} title="Add a 'Book Tickets' button that is a real link">
              Use the <span className="font-semibold">Website "Book Tickets" button</span> generator below. Paste the button code into an HTML block, or the "link only" into a Link element. The script automatically adds the visitor's campaign to it.
            </Step>
            <Step n={3} title="Make sure it's a link, not a JavaScript button">
              On no-code builders (Bubble, Wix, Squarespace) use a <span className="font-semibold">"Link" element</span> — not a button "action". Browsers only let the campaign ride across on a genuine link.
            </Step>
            <Step n={4} title="Publish and test it">
              Visit your site with a campaign link (e.g. <span className="st-mono">yoursite.com?utm_source=facebook</span>) and click Book Tickets. The ScareTickets address bar should now show <span className="st-mono">?utm_source=…</span>. Add <span className="st-mono">?st_debug=1</span> to see the script's log in the browser console (F12).
            </Step>
            <Step n={5} title="See your results">
              Bookings and drop-off by source & campaign appear in{" "}
              <RouterLink to="/dashboard/reports" className="text-[#dc2626] hover:underline" data-testid="tracking-help-reports">Reports → Marketing attribution</RouterLink>.
            </Step>
          </div>
        </div>
      )}
    </div>
  );
}

function BookingButtonGenerator({ attractionSlug, event }) {
  const [label, setLabel] = useState("Book Tickets");
  const [scope, setScope] = useState("attraction");  // attraction | event
  const [copied, setCopied] = useState("");

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const eventSlug = event?.slug;
  const link = attractionSlug
    ? `${origin}/${attractionSlug}${scope === "event" && eventSlug ? `/${eventSlug}` : ""}`
    : "";
  const btnLabel = label.trim() || "Book Tickets";
  const style = "display:inline-block;background:#dc2626;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-weight:700;font-size:16px;line-height:1;padding:14px 28px;border-radius:9999px;text-decoration:none;";
  const html = link ? `<a href="${link}" style="${style}">${escapeHtml(btnLabel)}</a>` : "";

  const copy = async (kind, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      notify.success(kind === "html" ? "Button code copied" : "Link copied");
      setTimeout(() => setCopied(""), 1800);
    } catch {
      notify.error("Couldn't copy — select and copy manually");
    }
  };

  return (
    <div className="st-card p-4 mt-6" data-testid="booking-button-generator">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 text-[#dc2626]"><ExternalLink size={20} /></div>
        <div>
          <div className="font-semibold">Website "Book Tickets" button</div>
          <p className="text-[13px] text-[#9a9a9a] mt-1">
            Add a booking button to your own website. Because it's a real link, the tracking script above
            automatically tags it with the visitor's campaign — no extra setup, and it works on every platform.
          </p>
        </div>
      </div>

      {!attractionSlug ? (
        <div className="text-xs text-[#7a7a7a]">Your booking link isn't ready yet.</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#8a8a8a]">Button text</label>
              <input className="st-input mt-1" value={label} placeholder="Book Tickets" data-testid="bb-label" onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-[#8a8a8a]">Links to</label>
              <div className="flex gap-1 bg-[#0e0e0e] border border-[#1a1a1a] rounded-[5px] p-0.5 mt-1 w-fit" data-testid="bb-scope">
                {[["attraction", "Your booking page"], ["event", eventSlug ? `This event` : "Event"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setScope(val)} disabled={val === "event" && !eventSlug}
                          data-testid={`bb-scope-${val}`}
                          className={`px-3 py-1.5 rounded-[3px] text-xs font-semibold transition-colors disabled:opacity-40 ${scope === val ? "bg-[#dc2626] text-white" : "text-[#8a8a8a] hover:text-white"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="text-xs text-[#8a8a8a]">Preview</label>
            <div className="mt-1 rounded-xl border border-[#1e1e1e] bg-[#0b0b0b] p-6 flex justify-center" data-testid="bb-preview">
              <span style={{ display: "inline-block", background: "#dc2626", color: "#fff", fontWeight: 700, fontSize: 16, lineHeight: 1, padding: "14px 28px", borderRadius: 9999 }}>
                {btnLabel}
              </span>
            </div>
          </div>

          <div className="mt-4">
            <label className="text-xs text-[#8a8a8a]">Button code <span className="text-[#5a5a5a]">— paste into a "custom HTML / embed" block on your site</span></label>
            <div className="flex items-center gap-2 mt-1">
              <input readOnly className="st-input flex-1 st-mono text-xs" value={html} data-testid="bb-html" onFocus={(e) => e.target.select()} />
              <button className="st-btn st-btn-primary text-sm shrink-0" onClick={() => copy("html", html)} data-testid="bb-copy-html">
                {copied === "html" ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy code</>}
              </button>
            </div>
          </div>

          <div className="mt-3">
            <label className="text-xs text-[#8a8a8a]">Link only <span className="text-[#5a5a5a]">— for a Link element in Wix, Squarespace, Bubble, etc.</span></label>
            <div className="flex items-center gap-2 mt-1">
              <input readOnly className="st-input flex-1 st-mono text-xs" value={link} data-testid="bb-link" onFocus={(e) => e.target.select()} />
              <button className="st-btn text-sm shrink-0" onClick={() => copy("link", link)} data-testid="bb-copy-link">
                {copied === "link" ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
              </button>
            </div>
          </div>

          <p className="text-[11px] text-[#7a7a7a] mt-3">
            Tip: attach a normal link (not a JavaScript button) so campaigns follow the visitor through. On no-code
            builders, use a "Link" element and paste the link above — don't use a button action.
          </p>
        </>
      )}
    </div>
  );
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function WebsiteTracking({ attractionSlug }) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const snippet = attractionSlug
    ? `<script src="${origin}/st.js" data-attraction="${attractionSlug}" async></script>`
    : "";
  const copy = async () => {
    if (!snippet) return;
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      notify.success("Tracking snippet copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      notify.error("Couldn't copy — select and copy manually");
    }
  };
  return (
    <div className="st-card p-4 mt-6" data-testid="website-tracking">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 text-[#dc2626]"><Link2 size={20} /></div>
        <div>
          <div className="font-semibold">Website tracking script</div>
          <p className="text-[13px] text-[#9a9a9a] mt-1">
            Running ads that point to your <span className="font-semibold">own website</span> first? Paste this snippet into your
            site's <span className="st-mono">&lt;head&gt;</span> (like Google Analytics). It captures the campaign tags when a
            visitor lands, then carries them through to your ScareTickets booking page — so the sale is attributed correctly.
          </p>
        </div>
      </div>
      {!attractionSlug ? (
        <div className="text-xs text-[#7a7a7a]">Your tracking snippet isn't ready yet.</div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <input readOnly className="st-input flex-1 st-mono text-xs" value={snippet} data-testid="tracking-snippet" onFocus={(e) => e.target.select()} />
            <button className="st-btn st-btn-primary text-sm shrink-0" onClick={copy} data-testid="tracking-copy">
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </button>
          </div>
          <p className="text-[11px] text-[#7a7a7a] mt-2">First-party only — it sets a cookie on your own domain and never uses third-party cookies. If your campaigns link straight to ScareTickets, you don't need this.</p>
        </>
      )}
    </div>
  );
}

function UtmBuilder({ attractionSlug, event, savedOwnUrl = "", onOwnUrlSaved }) {
  const [source, setSource] = useState("facebook");
  const [medium, setMedium] = useState("social");
  const [campaign, setCampaign] = useState("");
  const [term, setTerm] = useState("");
  const [content, setContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [dest, setDest] = useState("scaretickets");   // scaretickets | own
  const [ownUrl, setOwnUrl] = useState(savedOwnUrl);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const rootRef = React.useRef(null);

  // Prefill remembered website once it arrives from the attraction record.
  useEffect(() => { if (savedOwnUrl) setOwnUrl((cur) => cur || savedOwnUrl); }, [savedOwnUrl]);

  const loadLinks = useCallback(async () => {
    try { const r = await api.get("/marketing/utm-links"); setLinks(r.data || []); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadLinks(); }, [loadLinks]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const stBase = attractionSlug
    ? `${origin}/${attractionSlug}${event?.slug ? `/${event.slug}` : ""}`
    : "";
  const cleanOwn = ownUrl.trim().replace(/\?.*$/, "").replace(/\/$/, "");
  const base = dest === "own" ? cleanOwn : stBase;

  const params = new URLSearchParams();
  if (source.trim()) params.set("utm_source", source.trim().toLowerCase());
  if (medium.trim()) params.set("utm_medium", medium.trim().toLowerCase());
  if (campaign.trim()) params.set("utm_campaign", campaign.trim());
  if (term.trim()) params.set("utm_term", term.trim());
  if (content.trim()) params.set("utm_content", content.trim());
  const qs = params.toString();
  const url = base ? `${base}${qs ? `?${qs}` : ""}` : "";

  const copy = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notify.success("Tagged link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      notify.error("Couldn't copy — select and copy manually");
    }
  };

  const save = async () => {
    if (!url) { notify.error("Build a link first"); return; }
    if (!name.trim()) { notify.error("Give this link a name so you can find it later"); return; }
    if (dest === "own" && !cleanOwn) { notify.error("Enter your website URL"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), url,
        source: source.trim(), medium: medium.trim(), campaign: campaign.trim(), content: content.trim(),
        destination: dest, own_url: dest === "own" ? cleanOwn : "",
      };
      if (editingId) {
        await api.patch(`/marketing/utm-links/${editingId}`, payload);
        notify.success("Campaign link updated");
      } else {
        await api.post("/marketing/utm-links", payload);
        notify.success("Campaign link saved");
      }
      setName("");
      setEditingId(null);
      await loadLinks();
      if (dest === "own" && onOwnUrlSaved) onOwnUrlSaved();
    } catch (e) {
      notify.error(formatErr(e) || "Couldn't save link");
    } finally { setSaving(false); }
  };

  const editLink = (l) => {
    setEditingId(l.id);
    setName(l.name || "");
    setSource(l.source || "");
    setMedium(l.medium || "");
    setCampaign(l.campaign || "");
    setContent(l.content || "");
    setDest(l.destination === "own" ? "own" : "scaretickets");
    if (l.destination === "own" && l.own_url) setOwnUrl(l.own_url);
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEdit = () => { setEditingId(null); setName(""); };

  const remove = async (id) => {
    try {
      await api.delete(`/marketing/utm-links/${id}`);
      setLinks((l) => l.filter((x) => x.id !== id));
      if (editingId === id) cancelEdit();
    } catch { notify.error("Couldn't delete link"); }
  };

  const reportHref = (l) => {
    const p = new URLSearchParams();
    if (l.source) p.set("source", l.source);
    if (l.campaign) p.set("campaign", l.campaign);
    return `/dashboard/reports${p.toString() ? `?${p.toString()}` : ""}`;
  };

  return (
    <div ref={rootRef} className="st-card p-4 mt-6 scroll-mt-6" data-testid="utm-builder">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 text-[#dc2626]"><Link2 size={20} /></div>
        <div>
          <div className="font-semibold">Campaign link builder{editingId ? " · editing" : ""}</div>
          <p className="text-[13px] text-[#9a9a9a] mt-1">
            Generate tagged booking links for each channel. Visitors arriving via these links are
            attributed to the campaign in your Reports → Marketing attribution.
          </p>
        </div>
      </div>

      {!attractionSlug ? (
        <div className="text-xs text-[#7a7a7a]">Your booking link isn't ready yet.</div>
      ) : (
        <>
          <div className="mb-4">
            <label className="text-xs text-[#8a8a8a]">Where does this link point?</label>
            <div className="flex gap-1 bg-[#0e0e0e] border border-[#1a1a1a] rounded-[5px] p-0.5 mt-1 w-fit" data-testid="utm-dest">
              {[["scaretickets", "ScareTickets booking page"], ["own", "Via my own website"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setDest(val)} data-testid={`utm-dest-${val}`}
                        className={`px-3 py-1.5 rounded-[3px] text-xs font-semibold transition-colors ${dest === val ? "bg-[#dc2626] text-white" : "text-[#8a8a8a] hover:text-white"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            {dest === "own" && (
              <div className="mt-3">
                <input className="st-input" value={ownUrl} placeholder="https://your-website.com/tickets" data-testid="utm-own-url" onChange={(e) => setOwnUrl(e.target.value)} />
                <p className="text-[11px] text-[#d4a64a] mt-1.5">Point this at a page on your own site, then make sure the tracking script (above) is installed so the campaign follows visitors through to your ScareTickets booking page. We'll remember this URL for next time.</p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {UTM_PRESETS.map((p) => {
              const active = source === p.source && medium === p.medium;
              return (
                <button key={p.label} onClick={() => { setSource(p.source); setMedium(p.medium); }}
                        data-testid={`utm-preset-${p.source}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${active ? "bg-[#dc2626] text-white border-[#dc2626]" : "bg-[#0f0f0f] text-[#bbb] border-[#1e1e1e] hover:border-[#2a2a2a]"}`}>
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#8a8a8a]">Source</label>
              <input className="st-input mt-1" value={source} placeholder="e.g. facebook" data-testid="utm-source" onChange={(e) => setSource(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-[#8a8a8a]">Medium</label>
              <input className="st-input mt-1" value={medium} placeholder="e.g. social, cpc, email" data-testid="utm-medium" onChange={(e) => setMedium(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-[#8a8a8a]">Campaign</label>
              <input className="st-input mt-1" value={campaign} placeholder="e.g. halloween-launch, oct-flash-sale" data-testid="utm-campaign" onChange={(e) => setCampaign(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-[#8a8a8a]">Content <span className="text-[#5a5a5a]">(optional)</span></label>
              <input className="st-input mt-1" value={content} placeholder="e.g. story-ad, banner-a" data-testid="utm-content" onChange={(e) => setContent(e.target.value)} />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-xs text-[#8a8a8a]">Your tagged link</label>
            <div className="flex items-center gap-2 mt-1">
              <input readOnly className="st-input flex-1 st-mono text-xs" value={url} data-testid="utm-result" onFocus={(e) => e.target.select()} />
              <button className="st-btn text-sm shrink-0" onClick={() => copy(url)} data-testid="utm-copy">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            {!campaign.trim() && <p className="text-[11px] text-[#d4a64a] mt-1.5">Tip: add a campaign name to track this push separately in the funnel.</p>}
          </div>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-[#8a8a8a]">{editingId ? "Link name" : "Save as (name this link)"}</label>
              <input className="st-input mt-1" value={name} placeholder="e.g. October Facebook story ad" data-testid="utm-name" onChange={(e) => setName(e.target.value)} />
            </div>
            {editingId && (
              <button className="st-btn st-btn-ghost text-sm shrink-0" onClick={cancelEdit} data-testid="utm-cancel-edit">
                Cancel
              </button>
            )}
            <button className="st-btn st-btn-primary text-sm shrink-0" onClick={save} disabled={saving} data-testid="utm-save">
              <Save size={14} /> {saving ? "Saving…" : (editingId ? "Update link" : "Save link")}
            </button>
          </div>

          {/* Saved campaign links */}
          <div className="mt-6" data-testid="utm-saved">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Saved campaign links</div>
            {links.length === 0 ? (
              <div className="text-xs text-[#7a7a7a] border border-dashed border-[#1e1e1e] rounded-xl p-4" data-testid="utm-saved-empty">
                No saved links yet. Build one above and hit "Save link" to keep a named list here.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#1a1a1a]">
                <table className="w-full text-sm min-w-[620px]">
                  <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a] bg-[#0e0e0e]">
                    <tr>
                      <th className="py-2.5 px-3">Name</th>
                      <th className="px-2">Source / Campaign</th>
                      <th className="px-2">Destination</th>
                      <th className="px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((l) => (
                      <tr key={l.id} className="border-t border-[#1a1a1a] hover:bg-[#121212]" data-testid={`utm-row-${l.id}`}>
                        <td className="py-2.5 px-3">
                          <div className="font-semibold">{l.name}</div>
                          <div className="text-[10px] text-[#6a6a6a] st-mono truncate max-w-[280px]">{l.url}</div>
                        </td>
                        <td className="px-2">
                          <div className="capitalize">{l.source || "—"}</div>
                          <div className="text-[10px] text-[#7a7a7a]">{l.campaign || "no campaign tag"}</div>
                        </td>
                        <td className="px-2">
                          <span className="st-chip whitespace-nowrap">{l.destination === "own" ? "Own website" : "ScareTickets"}</span>
                        </td>
                        <td className="px-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button className="st-btn st-btn-ghost text-xs py-1 px-2" title="Copy link" onClick={() => copy(l.url)} data-testid={`utm-copy-${l.id}`}><Copy size={13} /></button>
                            <button className="st-btn st-btn-ghost text-xs py-1 px-2" title="Edit link" onClick={() => editLink(l)} data-testid={`utm-edit-${l.id}`}><Pencil size={13} /></button>
                            <RouterLink to={reportHref(l)} className="st-btn st-btn-ghost text-xs py-1 px-2" title="View report & metrics" data-testid={`utm-report-${l.id}`}><BarChart3 size={13} /> Report</RouterLink>
                            <button className="st-btn st-btn-ghost text-xs py-1 px-2 text-[#dc2626]" title="Delete" onClick={() => remove(l.id)} data-testid={`utm-delete-${l.id}`}><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
